import CalendarWeek from "./CalendarWeek";
import type { State, Slot } from "../state";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface CalendarMonthProps {
  state: State;
  config: { month: number; year: number };
  selectedDate: string;
  onDayClick: (dateStr: string) => void;
}

interface DayCell {
  dateStr: string;
  dayNum: number;
  slots: Slot[];
  isSelected: boolean;
  isOtherMonth: boolean;
}

export default function CalendarMonth({
  state,
  config,
  selectedDate,
  onDayClick,
}: CalendarMonthProps) {
  const { month, year } = config;
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const startWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = last.getDate();
  let cellCount = startWeekday + daysInMonth;
  if (cellCount % 7) cellCount += 7 - (cellCount % 7);

  const prevLast = new Date(year, month - 1, 0);
  const daysInPrev = prevLast.getDate();

  const weeks: DayCell[][] = [];
  let week: DayCell[] = [];
  for (let i = 0; i < cellCount; i++) {
    let dateStr = "";
    let dayNum = 0;
    let isOtherMonth = false;

    if (i < startWeekday) {
      const d = daysInPrev - startWeekday + 1 + i;
      const prevYear = month === 1 ? year - 1 : year;
      const prevMonth = month === 1 ? 12 : month - 1;
      dateStr = `${prevYear}-${String(prevMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      dayNum = d;
      isOtherMonth = true;
    } else if (i < startWeekday + daysInMonth) {
      dayNum = i - startWeekday + 1;
      dateStr = `${year}-${String(month).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    } else {
      const nextDay = i - startWeekday - daysInMonth + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const nextMonth = month === 12 ? 1 : month + 1;
      dateStr = `${nextYear}-${String(nextMonth).padStart(2, "0")}-${String(nextDay).padStart(2, "0")}`;
      dayNum = nextDay;
      isOtherMonth = true;
    }

    const slots = state.slotsByDate[dateStr] || [];
    week.push({
      dateStr,
      dayNum,
      slots,
      isSelected: dateStr === selectedDate,
      isOtherMonth,
    });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) weeks.push(week);

  return (
    <div className="flex flex-col flex-1 min-h-[280px] my-4 border-2 border-black dark:border-gray-100">
      <table className="w-full table-fixed border-collapse brutal-shadow bg-white dark:bg-gray-950">
        <thead>
          <tr>
            {WEEKDAYS.map((d) => (
              <th
                key={d}
                className="text-xs font-semibold text-center py-2 border-b-2 border-black dark:border-gray-100 bg-white dark:bg-gray-950"
              >
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((w, wi) => (
            <CalendarWeek
              key={wi}
              days={w}
              onDayClick={onDayClick}
              reactionsByUserBySlot={state.reactionsByUserBySlot}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
